import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "../../api";
import { createAsyncThunkInstance } from "../createAsyncThunkInstance";
import { ICommonInitialState } from "../../shared/types/storeTypes";
import {
  DepartmentsResponse,
  DepartmentRecruiterMove,
  DepartmentRecruiterAdd,
  DepartmentRecruiterRemove,
} from "@/api/departments/types";
import { AxiosError } from "axios";
import { Department } from "@/model/department";

interface IDeparmetnsState extends ICommonInitialState {
  departments: DepartmentsResponse;
  currentDepartment: Department | null;
}

const initialState: IDeparmetnsState = {
  departments: {
    count: 0,
    items: [],
  },
  currentDepartment: null,
  isLoading: false,
  error: null,
};

export const fetchAllDepartments = createAsyncThunkInstance<
  DepartmentsResponse,
  number | undefined
>("departments/fetchAllDepartments", (channelId?: number) =>
  api.departments.departments({
    params: {
      expand: ["manager", "recruitersCount", "recruitersCountActive"],
      channel_id: channelId,
    },
  })
);

export const fetchSingleDepartment = createAsyncThunkInstance<
  Department,
  number
>("departments/fetchSingleDepartment", (id) =>
  api.departments.departmentView({
    params: {
      id,
      expand: ["recruiters","canMoveRecruiter","canAddFreeRecruiter", "canRemoveRecruiter"],
    },
  })
);

export const addRecruiter = createAsyncThunk<
  undefined,
  DepartmentRecruiterAdd,
  { rejectValue: AxiosError }
>(
  "departments/addRecruiter",
  async ({ param, data }, { rejectWithValue }) => {
    try {
      const { data: responseData } = await api.departments.addRecruiter({
        param: {
          department_id: param.department_id,
        },
        data: {
          recruiter_id: data.recruiter_id,
        },
      });
      return responseData;
    } catch (error: unknown) {
      return rejectWithValue(error as AxiosError);
    }
  }
);

export const removeRecruiter = createAsyncThunk<
  undefined,
  DepartmentRecruiterRemove,
  { rejectValue: AxiosError }
>(
  "departments/removeRecruiter",
  async ({ param, data }, { rejectWithValue }) => {
    try {
      const { data: responseData } = await api.departments.removeRecruiter({
        param: {
          department_id: param.department_id,
        },
        data: {
          recruiter_id: data.recruiter_id,
        },
      });
      return responseData;
    } catch (error: unknown) {
      return rejectWithValue(error as AxiosError);
    }
  }
);

export const moveRecruiter = createAsyncThunk<
  undefined,
  DepartmentRecruiterMove,
  { rejectValue: AxiosError }
>(
  "departments/moveRecruiter",
  async ({ param, data }, { rejectWithValue }) => {
    try {
      const { data: responseData } = await api.departments.moveRecruiter({
        param: {
          department_id: param.department_id,
        },
        data: {
          recruiter_id: data.recruiter_id,
        },
      });
      return responseData;
    } catch (error: unknown) {
      return rejectWithValue(error as AxiosError);
    }
  }
);

const departmentsSlice = createSlice({
  name: "departments",
  initialState,
  reducers: {
    resetCurrentDepartment: (state) => {
      state.currentDepartment = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllDepartments.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAllDepartments.fulfilled, (state, action) => {
        state.isLoading = false;
        state.departments = action.payload;
      })
      .addCase(fetchSingleDepartment.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSingleDepartment.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentDepartment = action.payload;
      });
  },
});

export const { resetCurrentDepartment } = departmentsSlice.actions;
export const departmentsReducer = departmentsSlice.reducer;
